import { useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Heart, Users, Camera, Music, Utensils, Star, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SEOHead, EventVenueSchema, BreadcrumbSchema } from '../components/SEO';

const eventTypes = [
  { id: 'wedding', name: 'Wedding', icon: Heart, description: 'Intimate ceremonies to grand celebrations' },
  { id: 'corporate', name: 'Corporate Events', icon: Users, description: 'Conferences, retreats & team building' },
  { id: 'celebration', name: 'Private Celebrations', icon: Camera, description: 'Birthdays, anniversaries & milestones' },
];

const venues = [
  {
    name: 'The Grand Ballroom',
    capacity: '300 guests',
    image: 'https://images.pexels.com/photos/33914530/pexels-photo-33914530.jpeg',
    features: ['Crystal Chandeliers', 'Dance Floor', 'Stage & AV'],
  },
  {
    name: 'Garden Pavilion',
    capacity: '150 guests',
    image: 'https://images.pexels.com/photos/29347337/pexels-photo-29347337.jpeg',
    features: ['Open Air', 'Mountain Views', 'Sunset Backdrop'],
  },
  {
    name: 'The Sky Deck',
    capacity: '80 guests',
    image: 'https://images.unsplash.com/photo-1745549670488-6852ef218009',
    features: ['Rooftop Setting', 'Panoramic Views', 'Intimate Ambiance'],
  },
];

const testimonials = [
  {
    couple: 'Arjun & Priya',
    event: 'Wedding, December 2024',
    text: 'Our dream wedding became a reality at The Hotel Emerald. The team handled every detail perfectly, from the décor to the catering. Our guests are still talking about the breathtaking venue.',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
  },
  {
    couple: 'Infosys Technologies',
    event: 'Annual Retreat, November 2024',
    text: 'Excellent facilities for our 200-person corporate retreat. The conference rooms were well-equipped, and the team building activities were thoughtfully organized. Highly recommended.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
  },
];

export default function EventsPage() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // In a real app, this would submit to the backend
    toast.success('Inquiry submitted! Our events team will contact you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      eventType: '',
      eventDate: '',
      guestCount: '',
      message: '',
    });
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Events & Weddings', url: 'https://thehotelemerald.in/events' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="events-page">
      {/* SEO Components */}
      <SEOHead 
        title="Events & Weddings | The Hotel Emerald - Premier Venue in Odisha"
        description="Host your dream wedding or corporate event at The Hotel Emerald. Stunning mountain views, capacity up to 500 guests, full-service catering & event planning."
        keywords="wedding venue odisha, corporate retreat koraput, destination wedding eastern ghats, event venue semiliguda"
        url="https://thehotelemerald.in/events"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <EventVenueSchema />
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/33914530/pexels-photo-33914530.jpeg"
            alt="Wedding Venue"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/50" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overline text-gold mb-3"
          >
            Create Memories
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4"
          >
            Events & Weddings
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-emerald-100 max-w-xl mx-auto"
          >
            Where every celebration becomes an unforgettable experience
          </motion.p>
        </div>
      </section>

      {/* Event Types */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">What We Offer</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              Event Services
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {eventTypes.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 bg-card border border-border rounded-sm hover:border-emerald-700/50 transition-colors"
                data-testid={`event-type-${event.id}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center">
                  <event.icon className="w-8 h-8 text-emerald-700 dark:text-emerald-400" />
                </div>
                <h3 className="font-serif text-xl text-foreground mb-2">{event.name}</h3>
                <p className="text-muted-foreground text-sm">{event.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Venues */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Our Spaces</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              Event Venues
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {venues.map((venue, index) => (
              <motion.div
                key={venue.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`venue-${venue.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="relative aspect-[4/3] rounded-sm overflow-hidden mb-4">
                  <img
                    src={venue.image}
                    alt={venue.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-serif text-xl">{venue.name}</h3>
                    <p className="text-emerald-200 text-sm flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Up to {venue.capacity}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {venue.features.map((feature) => (
                    <span key={feature} className="text-xs bg-muted px-2 py-1 rounded-sm">
                      {feature}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Success Stories</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto relative">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-card border border-border rounded-sm p-8 text-center"
            >
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                ))}
              </div>
              <p className="text-lg text-foreground font-serif italic mb-6">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="flex items-center justify-center gap-4">
                <img
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].couple}
                  className="w-12 h-12 rounded-full object-cover border-2 border-gold"
                />
                <div className="text-left">
                  <p className="font-serif text-foreground">{testimonials[currentTestimonial].couple}</p>
                  <p className="text-sm text-muted-foreground">{testimonials[currentTestimonial].event}</p>
                </div>
              </div>
            </motion.div>

            <div className="flex justify-center gap-4 mt-6">
              <Button variant="outline" size="icon" onClick={prevTestimonial} data-testid="event-testimonial-prev">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="icon" onClick={nextTestimonial} data-testid="event-testimonial-next">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="section-padding bg-emerald-950 text-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="overline text-gold mb-3">Get In Touch</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Plan Your Event
              </h2>
              <p className="text-emerald-200 mb-8">
                Let our experienced events team help you create the perfect celebration. Fill out the inquiry form and we'll get back to you within 24 hours with a personalized proposal.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-800 rounded-sm flex items-center justify-center">
                    <Utensils className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-white">Customized Menus</p>
                    <p className="text-emerald-300 text-sm">From local to international cuisines</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-800 rounded-sm flex items-center justify-center">
                    <Music className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-white">Entertainment</p>
                    <p className="text-emerald-300 text-sm">Live music, DJs & cultural performances</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-800 rounded-sm flex items-center justify-center">
                    <Camera className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-white">Photography</p>
                    <p className="text-emerald-300 text-sm">Professional photo & video services</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="bg-emerald-900/50 backdrop-blur rounded-sm p-6 space-y-4" data-testid="event-inquiry-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-emerald-200">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-emerald-800/50 border-emerald-700 text-white"
                      required
                      data-testid="event-form-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-emerald-200">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-emerald-800/50 border-emerald-700 text-white"
                      required
                      data-testid="event-form-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-emerald-200">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-emerald-800/50 border-emerald-700 text-white"
                      required
                      data-testid="event-form-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="eventType" className="text-emerald-200">Event Type</Label>
                    <Select value={formData.eventType} onValueChange={(v) => setFormData({...formData, eventType: v})}>
                      <SelectTrigger className="bg-emerald-800/50 border-emerald-700 text-white" data-testid="event-form-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wedding">Wedding</SelectItem>
                        <SelectItem value="corporate">Corporate Event</SelectItem>
                        <SelectItem value="celebration">Private Celebration</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="eventDate" className="text-emerald-200">Preferred Date</Label>
                    <Input
                      id="eventDate"
                      type="date"
                      value={formData.eventDate}
                      onChange={(e) => setFormData({...formData, eventDate: e.target.value})}
                      className="bg-emerald-800/50 border-emerald-700 text-white"
                      data-testid="event-form-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="guestCount" className="text-emerald-200">Expected Guests</Label>
                    <Select value={formData.guestCount} onValueChange={(v) => setFormData({...formData, guestCount: v})}>
                      <SelectTrigger className="bg-emerald-800/50 border-emerald-700 text-white" data-testid="event-form-guests">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-50">1-50</SelectItem>
                        <SelectItem value="51-100">51-100</SelectItem>
                        <SelectItem value="101-200">101-200</SelectItem>
                        <SelectItem value="200+">200+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-emerald-200">Additional Details</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="bg-emerald-800/50 border-emerald-700 text-white min-h-[100px]"
                    placeholder="Tell us about your vision..."
                    data-testid="event-form-message"
                  />
                </div>

                <Button type="submit" className="w-full bg-gold hover:bg-gold-light text-emerald-950" data-testid="event-form-submit">
                  <Send className="w-4 h-4 mr-2" />
                  Submit Inquiry
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
